import type { ReadonlySignal } from "@preact/signals";
import type { FormStore } from "@formisch/preact";
import { setInput } from "@formisch/preact";

import { usesCheckboxOptions } from "../../checkbox-options.js";
import { useId } from "preact/hooks";
import { InputError } from "./input-error.js";
import type { FieldProps } from "./shared.js";
import type { FormEmbedField } from "../../types.js";

interface CheckboxProps {
  field: FormEmbedField;
  input: ReadonlySignal<any>;
  errors: ReadonlySignal<[string, ...string[]] | null>;
  props: FieldProps;
  disabled: boolean;
  form: FormStore;
}

export function Checkbox({ field, input, errors, props, disabled, form }: CheckboxProps) {
  const descriptionId = `${useId()}-description`;
  if (usesCheckboxOptions(field)) {
    const selected: string[] = Array.isArray(input.value) ? input.value : [];
    return (
      <fieldset class={`flex flex-col gap-1 border-0 p-0 m-0 min-w-0 ${field.width === "half" ? "w-[calc(50%-6px)]" : "w-full"}`} data-fv-field={field.name} aria-describedby={field.description ? descriptionId : undefined}>
        <legend class="text-sm font-medium text-theme-fg">
          {field.label}
          {field.required && <span class="text-theme-destructive"> *</span>}
        </legend>
        {field.description && <span id={descriptionId} class="text-xs text-theme-muted-fg">{field.description}</span>}
        <div class={field.type === "pillCheckbox" ? "flex flex-wrap gap-2 mt-1" : "flex flex-col gap-3 mt-1"}>
          {field.options?.map((option) => (
            <label key={option.value} class={`flex items-center text-theme-fg cursor-pointer ${field.type === "pillCheckbox" ? "gap-2 text-sm rounded-full border px-3 py-2" : "min-h-12 w-full gap-3 px-3 py-3 text-base"}`}>
              <input
                type="checkbox"
                class="w-5 h-5 shrink-0"
                name={props.name}
                value={option.value}
                checked={selected.includes(option.value)}
                disabled={disabled}
                style={{ accentColor: "var(--fv-theme-primary, var(--fv-button-bg, #007145))" }}
                onChange={(event) => setInput(form, {
                  path: [field.name] as any,
                  input: (event.target as HTMLInputElement).checked
                    ? field.multiSelect === false ? [option.value] : [...selected, option.value]
                    : selected.filter((value) => value !== option.value),
                })}
                onBlur={props.onBlur as any}
                aria-describedby={field.description ? descriptionId : undefined}
                aria-invalid={!!errors.value}
                aria-errormessage={`${props.name}-error`}
              />
              {option.label}
            </label>
          ))}
        </div>
        <InputError name={props.name} errors={errors} />
      </fieldset>
    );
  }
  return (
    <div class={`flex flex-col gap-1 ${field.width === "half" ? "w-[calc(50%-6px)]" : "w-full"}`} data-fv-field={field.name}>
      <label class="flex items-center gap-2 text-sm text-theme-fg cursor-pointer">
        <input
          {...props}
          type="checkbox"
          class="w-4 h-4 shrink-0 cursor-pointer"
          style={{ accentColor: "var(--fv-theme-primary, var(--fv-button-bg, #007145))" }}
          checked={!!input.value}
          disabled={disabled}
          onChange={(e: Event) => setInput(form, { path: [field.name] as any, input: (e.target as HTMLInputElement).checked })}
          aria-invalid={!!errors.value}
          aria-errormessage={`${props.name}-error`}
        />
        <span>
          {field.label}
          {field.required && <span class="text-theme-destructive"> *</span>}
        </span>
      </label>
      <InputError name={props.name} errors={errors} />
    </div>
  );
}
